/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      // Conditional Options Konstanten
      this.CUSTOM_SIZE_VALUE = "✂ Maßanfertigung";
      this.STANDARD_SIZE_VALUE = "Standardgrößen";
      
      // Flag für automatische Variantenauswahl (kann über data-Attribut gesteuert werden)
      this.autoSelectAvailableVariants = this.dataset.autoSelectVariants !== 'false';

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
        this.initConditionalOptions();
      });
    }

    /**
     * Initialisiert die Conditional Options Funktionalität
     */
    initConditionalOptions() {
      // Initiale Filterung nach kurzem Delay
      setTimeout(() => {
        this.filterConditionalOptions();
        // Stelle sicher, dass eine verfügbare Variante ausgewählt ist
        this.ensureAvailableVariantSelected();
      }, 10);
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      // Conditional Options Filterung bei Änderungen
      if (evt.target.type === 'radio') {
        this.filterConditionalOptions();
      }

      // WICHTIG: Erst nach der Auswahl prüfen und automatisch anpassen
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      // Stelle sicher, dass immer eine verfügbare Variante ausgewählt ist
      // NACH der aktuellen Auswahl, um andere Optionen anzupassen
      this.ensureAvailableVariantSelected(evt);

      // Aktualisiere nach möglichen automatischen Änderungen
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      // Availability NACH der automatischen Anpassung updaten
      this.updateAvailability();

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Stellt sicher, dass immer eine verfügbare Variante ausgewählt ist
     */
    ensureAvailableVariantSelected(evt) {
      // Prüfe ob aktuelle Variante verfügbar ist
      if (this.variant && this.variant.available) {
        return; // Alles gut, verfügbare Variante ist ausgewählt
      }

      // Wenn gerade eine neue Option ausgewählt wurde, versuche diese zu behalten
      // und passe die anderen Optionen entsprechend an
      let targetSelection = null;
      if (evt && evt.target && evt.target.type === 'radio') {
        targetSelection = {
          value: evt.target.value,
          selector: evt.target.closest('.option-selector')
        };
      }

      // Finde alle sichtbaren Optionen
      const visibleOptions = Array.from(this.querySelectorAll('label[data-option-value]'))
        .filter(label => label.style.display !== 'none');

      // Finde die beste verfügbare Variante
      const bestVariant = this.findBestAvailableVariantKeepingTarget(visibleOptions, targetSelection);
      
      if (bestVariant && (!this.variant || bestVariant.id !== this.variant.id)) {
        // Verhindere Endlosschleife durch temporäres Entfernen des Event Listeners
        this.removeEventListener('change', this.handleVariantChange.bind(this));
        
        // Wähle die beste verfügbare Variante aus
        this.selectVariantSilently(bestVariant);
        
        // Event Listener wieder hinzufügen
        this.addEventListener('change', this.handleVariantChange.bind(this));
      }
    }

    /**
     * Findet die beste verfügbare Variante und behält dabei die Zielauswahl bei
     */
    findBestAvailableVariantKeepingTarget(visibleOptions, targetSelection) {
      const availableVariants = this.findAvailableVariants();
      if (availableVariants.length === 0) return null;

      // Wenn eine spezifische Auswahl gemacht wurde, versuche diese beizubehalten
      if (targetSelection) {
        const targetOptionName = targetSelection.selector?.dataset.optionName;
        const targetValue = targetSelection.value;

        // Finde Varianten die diese spezifische Auswahl enthalten
        const variantsWithTarget = availableVariants.filter(variant => {
          return variant.options.includes(targetValue);
        });

        if (variantsWithTarget.length > 0) {
          // Finde die beste Variante mit der Zielauswahl
          for (const variant of variantsWithTarget) {
            let allOptionsVisible = true;
            variant.options.forEach((optionValue) => {
              const isVisible = visibleOptions.some(visibleOption => 
                visibleOption.dataset.optionValue === optionValue
              );
              if (!isVisible) allOptionsVisible = false;
            });
            
            if (allOptionsVisible) return variant;
          }

          // Falls nicht alle Optionen sichtbar sind, nimm die erste verfügbare mit der Zielauswahl
          return variantsWithTarget[0];
        }
      }

      // Fallback auf die normale Logik
      return this.findBestAvailableVariant(visibleOptions);
    }

    /**
     * Wählt eine Variante aus ohne Events zu triggern
     */
    selectVariantSilently(variant) {
      if (!variant || !variant.options) return;
      
      variant.options.forEach((optionValue, index) => {
        const radioButton = this.querySelector(
          `input[type="radio"][data-option-value="${optionValue}"]`
        );
        
        if (radioButton && radioButton.style.display !== 'none') {
          radioButton.checked = true;
          // Keine Events triggern hier
        }
      });

      this.updateColorText();
    }

    /**
     * Filtert Optionen basierend auf Größenauswahl (Conditional Options)
     */
    filterConditionalOptions() {
      // Suche nach der Größenauswahl-Option
      const sizeTypeInputs = this.querySelectorAll('input[type="radio"]');
      let selectedSizeType = null;
      let sizeTypeFound = false;
      
      // Überprüfe ob es überhaupt eine Größenauswahl gibt
      sizeTypeInputs.forEach(input => {
        const value = input.value;
        if (value === this.CUSTOM_SIZE_VALUE || value === this.STANDARD_SIZE_VALUE) {
          sizeTypeFound = true;
          
          if (input.checked) {
            selectedSizeType = value === this.CUSTOM_SIZE_VALUE ? 'custom' : 'standard';
          }
        }
      });

      // Wenn keine Größenauswahl vorhanden ist, nichts filtern
      if (!sizeTypeFound) return;

      const visibleOptions = [];

      // Wenn Größenauswahl vorhanden, aber nichts ausgewählt, alle anzeigen
      if (!selectedSizeType) {
        const allLabels = this.querySelectorAll('label[data-option-value]');
        allLabels.forEach(label => {
          label.style.display = '';
          visibleOptions.push(label);
        });
        return;
      }

      // Farb-Optionen sind immer sichtbar
      const colorOptionSelectors = this.querySelectorAll(
        '.option-selector[data-option-name*="farbe"], .option-selector[data-option-name*="color"]'
      );
      colorOptionSelectors.forEach(colorSelector => {
        const colorLabels = colorSelector.querySelectorAll('label[data-option-value]');
        colorLabels.forEach(label => {
          label.style.display = '';
          visibleOptions.push(label);
        });
      });

      // Nur die Maße/Größen-Optionen filtern
      const sizeOptionSelectors = this.querySelectorAll(
        '.option-selector[data-option-name*="maße"], .option-selector[data-option-name*="größe"], .option-selector[data-option-name*="size"]'
      );

      if (sizeOptionSelectors.length === 0) return;

      sizeOptionSelectors.forEach(sizeSelector => {
        const labels = sizeSelector.querySelectorAll('label[data-option-value]');
        
        labels.forEach(label => {
          const optionValue = label.dataset.optionValue || '';
          const optionText = optionValue.toLowerCase();
          
          const isBreiteOption = optionText.includes('breite');
          const isStandardSize = /^\d+x\d+/.test(optionText) || /\d+x\d+\s*cm/.test(optionText);
          const isGanzeRolle = optionText.includes('ganze rolle') || optionText.includes('rolle');
          
          // Zeige/Verstecke basierend auf Auswahl
          if (selectedSizeType === 'custom') {
            // Maßanfertigung: nur Breite-Optionen anzeigen, alle anderen verstecken
            if (isBreiteOption) {
              label.style.display = '';
              visibleOptions.push(label);
            } else if (isStandardSize || isGanzeRolle) {
              label.style.display = 'none';
            }
          } else if (selectedSizeType === 'standard') {
            // Standardgrößen: Standard-Größen UND Ganze Rolle anzeigen, Breite verstecken
            if ((isStandardSize || isGanzeRolle) && !isBreiteOption) {
              label.style.display = '';
              visibleOptions.push(label);
            } else if (isBreiteOption) {
              label.style.display = 'none';
            }
          }
        });
      });

      // Nach dem Filtern: Prüfe ob aktuell ausgewählte Optionen noch sichtbar sind
      const checkedInputs = this.querySelectorAll('input[type="radio"]:checked');
      let hasHiddenSelections = false;
      
      checkedInputs.forEach(input => {
        const label = this.querySelector(`label[for="${input.id}"]`);
        if (label && label.style.display === 'none') {
          input.checked = false;
          hasHiddenSelections = true;
        }
      });

      // Ein-/Ausblenden der Preis- und Info-Blöcke basierend auf Größenauswahl
      this.togglePriceAndInfoBlocks(selectedSizeType);

      // Wenn versteckte Selektionen gefunden wurden oder keine Auswahl vorhanden ist,
      // stelle sicher dass eine verfügbare Variante ausgewählt ist
      if (hasHiddenSelections || checkedInputs.length === 0) {
        // Kurzer Timeout um sicherzustellen dass alle DOM-Updates abgeschlossen sind
        setTimeout(() => {
          this.ensureAvailableVariantSelected();
        }, 10);
      }
    }

    /**
     * Findet verfügbare Varianten
     */
    findAvailableVariants() {
      if (!this.data || !this.data.product) return [];
      return this.data.product.variants.filter(variant => variant.available);
    }

    /**
     * Findet die beste verfügbare Variante basierend auf sichtbaren Optionen
     */
    findBestAvailableVariant(visibleOptions) {
      const availableVariants = this.findAvailableVariants();
      if (availableVariants.length === 0) return null;

      // Erstelle eine Map der sichtbaren Optionen pro Kategorie
      const visibleOptionsByName = {};
      visibleOptions.forEach(option => {
        const optionSelector = option.closest('.option-selector');
        const optionName = optionSelector?.dataset.optionName;
        if (optionName) {
          if (!visibleOptionsByName[optionName]) {
            visibleOptionsByName[optionName] = [];
          }
          visibleOptionsByName[optionName].push(option.dataset.optionValue);
        }
      });

      // Sammle aktuell ausgewählte Optionen
      const currentSelections = this.getSelectedOptions();
      
      // Priorität 1: Variante die exakt zu aktuellen Selektionen passt (falls verfügbar)
      for (const variant of availableVariants) {
        if (variant.options.every((option, index) => option === currentSelections[index])) {
          // Prüfe ob alle Optionen dieser Variante sichtbar sind
          let allOptionsVisible = true;
          variant.options.forEach((optionValue, index) => {
            const isVisible = visibleOptions.some(visibleOption => 
              visibleOption.dataset.optionValue === optionValue
            );
            if (!isVisible) allOptionsVisible = false;
          });
          
          if (allOptionsVisible) return variant;
        }
      }

      // Priorität 2: Variante die zu möglichst vielen aktuellen Selektionen passt
      let bestMatch = null;
      let bestMatchScore = -1;

      for (const variant of availableVariants) {
        let score = 0;
        let allOptionsVisible = true;
        
        // Prüfe ob alle Optionen dieser Variante sichtbar sind
        variant.options.forEach((optionValue, index) => {
          const isVisible = visibleOptions.some(visibleOption => 
            visibleOption.dataset.optionValue === optionValue
          );
          if (!isVisible) {
            allOptionsVisible = false;
          }
          
          // Score für übereinstimmende Optionen
          if (optionValue === currentSelections[index]) {
            score += 10;
          }
        });

        if (allOptionsVisible && score > bestMatchScore) {
          bestMatch = variant;
          bestMatchScore = score;
        }
      }

      if (bestMatch) return bestMatch;

      // Priorität 3: Erste verfügbare Variante deren Optionen alle sichtbar sind
      for (const variant of availableVariants) {
        let allOptionsVisible = true;
        variant.options.forEach((optionValue) => {
          const isVisible = visibleOptions.some(visibleOption => 
            visibleOption.dataset.optionValue === optionValue
          );
          if (!isVisible) allOptionsVisible = false;
        });
        
        if (allOptionsVisible) return variant;
      }

      // Fallback: Erste verfügbare Variante
      return availableVariants[0];
    }

    /**
     * Wählt eine bestimmte Variante aus (mit Events)
     */
    selectVariant(variant) {
      if (!variant || !variant.options) return;
      
      variant.options.forEach((optionValue, index) => {
        const radioButton = this.querySelector(
          `input[type="radio"][data-option-value="${optionValue}"]`
        );
        
        if (radioButton && radioButton.style.display !== 'none') {
          radioButton.checked = true;
          radioButton.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      this.updateColorText();
    }

/**
 * Ein-/Ausblenden der Preis- und Info-Blöcke
 */
togglePriceAndInfoBlocks(selectedSizeType) {
  const productForm = document.querySelector('product-form') || 
                    document.querySelector('form[action*="/cart/add"]');
  const priceBlock = document.querySelector('.product-info__block.product-info__block--sm.product-price');
  
  // Das fx-dynamic-pricing-container Element finden
  const dynamicPricingContainer = document.querySelector('.fx-dynamic-pricing-container') ||
                                 document.querySelector('[class*="fx-dynamic-pricing"]');

  if (selectedSizeType === 'custom') {
    // Maßanfertigung: Form UND Preis-Block ausblenden, Dynamic Pricing einblenden
    if (productForm) productForm.style.display = 'none';
    if (priceBlock) priceBlock.style.display = 'none';
    if (dynamicPricingContainer) dynamicPricingContainer.style.display = ''; // EINBLENDEN
  } else if (selectedSizeType === 'standard') {
    // Standardgrößen: Form und Preis-Block einblenden, Dynamic Pricing ausblenden
    if (productForm) productForm.style.display = '';
    if (priceBlock) priceBlock.style.display = '';
    if (dynamicPricingContainer) dynamicPricingContainer.style.display = 'none'; // AUSBLENDEN
  } else {
    // Keine Auswahl: Alles einblenden
    if (productForm) productForm.style.display = '';
    if (priceBlock) priceBlock.style.display = '';
    if (dynamicPricingContainer) dynamicPricingContainer.style.display = '';
  }
}

    /**
     * Aktualisiert den Farbtext
     */
    updateColorText() {
      const colorTextElements = this.querySelectorAll('.js-color-text');
      colorTextElements.forEach(element => {
        const checkedInput = element.closest('.option-selector')?.querySelector('input[type="radio"]:checked');
        if (checkedInput) {
          element.textContent = checkedInput.value;
        }
      });
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {
        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option (erweiterte Version).
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     * @param {boolean} allowSelection - Allow selection even if not available (for auto-select mode)
     */
    static updateOptionAvailability(optionEl, exists, available, allowSelection = false) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      
      // Zeige visuelle Hinweise auf Verfügbarkeit
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      // Aber blockiere die Auswahl nicht wenn allowSelection aktiv ist
      if (allowSelection) {
        // Entferne disabled Attribute falls vorhanden
        el.removeAttribute('disabled');
        if (el.nextElementSibling) {
          el.nextElementSibling.style.pointerEvents = '';
        }
      }

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        // Zeige Tooltip für nicht verfügbare Optionen
        el.nextElementSibling.title = unavailableText;
        
        // Aber erlaube trotzdem Klicks wenn allowSelection aktiv ist
        if (allowSelection) {
          el.nextElementSibling.style.pointerEvents = '';
        }
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false, this.autoSelectAvailableVariants);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available, this.autoSelectAvailableVariants);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable, this.autoSelectAvailableVariants);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update price.
        if (variant.compare_at_price > variant.price) {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.salePrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          if (priceWasEl) {
            priceWasEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
            priceWasEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
          }
        } else {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          if (priceWasEl) {
            priceWasEl.querySelector('.js-label').textContent = '';
            priceWasEl.querySelector('.js-value').innerHTML = '';
          }
        }

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}